/**
 * Progress Page - "My SAT Journey"
 *
 * Comprehensive progress tracking page showing:
 * - Predicted SAT score with trend
 * - Current level and progress
 * - Achievement badges
 * - Goal setting
 * - Personalized recommendations based on performance
 */

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../stores/authStore'
import {
  TrendingUp,
  Target,
  Trophy,
  Award,
  ChevronRight,
  Share2,
  BarChart2,
  Sparkles,
  AlertCircle,
  CheckCircle,
  BookOpen,
  Lightbulb,
  Download,
  BarChart3,
  ClipboardList,
  RefreshCw
} from 'lucide-react'
import LoadingSpinner from '../components/LoadingSpinner'
import {
  PerSubjectScoreDisplay,
  CombinedTrendChart,
  GoalSettingModal,
  ShareProgressModal,
  PracticePlanCard,
  StudyPlanProjectionChart
} from '../components/scores'
import { usePerSubjectScores } from '../hooks/usePerSubjectScores'
import { useCombinedActualHistory } from '../hooks/useActualHistory'
import AIAdvisorCard from '../components/dashboard/AIAdvisorCard'
import BadgeDisplay from '../components/badges/BadgeDisplay'
import StudentGroupBadges from '../components/adaptiveTeaching/StudentGroupBadges'
import StrategyLevelCard, { StrategyLevelCardEmpty } from '../components/StrategyLevelCard'
import availableStrategiesService from '../services/availableStrategiesService'
import api from '../services/api'
import reportService from '../services/reportService'
import toast from 'react-hot-toast'
import { createLogger } from '../utils/logger'

const logger = createLogger('Progress')

const Progress = () => {
  const { user, strategyLevels, fetchStrategyLevels } = useAuthStore()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [availableStrategies, setAvailableStrategies] = useState([])
  const [goalModalOpen, setGoalModalOpen] = useState(false)
  const [shareModalOpen, setShareModalOpen] = useState(false)
  const [userGoal, setUserGoal] = useState(null)
  const [userTestDate, setUserTestDate] = useState(null)
  const [predictedScore, setPredictedScore] = useState(null)
  const [recommendations, setRecommendations] = useState(null)
  const [recommendationsLoading, setRecommendationsLoading] = useState(false)
  const [practiceRecommendation, setPracticeRecommendation] = useState(null)
  const [studyPlan, setStudyPlan] = useState(null)
  const [downloadingReport, setDownloadingReport] = useState(false)
  const [scoreReports, setScoreReports] = useState([])
  // Resilience: distinguish "goal/prediction fetch failed" from "no goal set" so a transient
  // failure surfaces an error + Retry instead of silently looking like the data was reset.
  const [loadError, setLoadError] = useState(false)
  const [goalLoaded, setGoalLoaded] = useState(false)

  // Per-subject scores hook for reflective display
  const {
    loading: perSubjectLoading,
    error: perSubjectError,
    refresh: refreshPerSubject,
    math,
    readingWriting,
    combined
  } = usePerSubjectScores()

  // Actual challenge score history for trend chart
  const {
    math: mathActual,
    readingWriting: rwActual,
    loading: actualHistoryLoading
  } = useCombinedActualHistory()

  const loadData = useCallback(async () => {
    try {
      setLoading(true)
      setLoadError(false)
      const [, strategies] = await Promise.all([
        fetchStrategyLevels(),
        availableStrategiesService.getAvailableStrategies()
      ])
      setAvailableStrategies(strategies)

      // Fetch goal, prediction, and score reports independently. allSettled (not all) so one
      // failing endpoint never discards a sibling's good data - a failed /predictions/combined
      // must not wipe a successfully loaded goal.
      const [goalResult, predictionResult, reportsResult] = await Promise.allSettled([
        api.get('/predictions/goal'),
        api.get('/predictions/combined'),
        api.get('/score-reports', { params: { limit: 50 } })
      ])

      let hadError = false

      if (goalResult.status === 'fulfilled' && goalResult.value?.data?.success) {
        setUserGoal(goalResult.value.data.data?.goalSatScore || null)
        setUserTestDate(goalResult.value.data.data?.testDate || null)
        setGoalLoaded(true)
      } else if (goalResult.status === 'rejected') {
        hadError = true
      }

      if (
        predictionResult.status === 'fulfilled' &&
        predictionResult.value?.data?.success &&
        predictionResult.value.data.data?.hasEnoughData
      ) {
        setPredictedScore(predictionResult.value.data.data.predictedTotal)
      } else if (predictionResult.status === 'rejected') {
        hadError = true
      }

      if (
        reportsResult.status === 'fulfilled' &&
        reportsResult.value?.data?.success &&
        reportsResult.value.data.reports
      ) {
        setScoreReports(reportsResult.value.data.reports)
      }

      // Fetch recommendations and practice plan
      setRecommendationsLoading(true)
      try {
        // Fetch each independently so one failing endpoint never discards a sibling's good data -
        // the same resilience the outer goal/prediction batch gets via allSettled. Previously a
        // rejected practice-recommendation (e.g. a stored test date now in the past → 400) took
        // recommendations down with it and blanked the whole panel.
        const [recResponse, practiceResponse, studyPlanResponse] = await Promise.all([
          api.get('/predictions/recommendations').catch(() => null),
          api.get('/predictions/practice-recommendation').catch(() => null),
          api.get('/predictions/study-plan').catch(() => null)
        ])
        if (recResponse?.data?.success) {
          setRecommendations(recResponse.data.data)
        }
        if (practiceResponse?.data?.success) {
          setPracticeRecommendation(practiceResponse.data.data)
        }
        if (studyPlanResponse?.data?.success) {
          setStudyPlan(studyPlanResponse.data.data)
        }
      } catch (recError) {
        logger.warn('Could not fetch recommendations:', recError)
      } finally {
        setRecommendationsLoading(false)
      }

      if (hadError) setLoadError(true)
    } catch (error) {
      logger.error('Error loading progress data:', error)
      setLoadError(true)
    } finally {
      setLoading(false)
    }
  }, [fetchStrategyLevels])

  useEffect(() => {
    loadData()
  }, [loadData])

  const handleDownloadReport = async () => {
    setDownloadingReport(true)
    try {
      const result = await reportService.generateAndDownloadMyReport({
        period: 'all_time'
      })

      if (result && result.blob) {
        reportService.triggerDownload(result.blob, result.filename)
        toast.success('Progress report downloaded successfully')
      }
    } catch (error) {
      logger.error('Failed to download report:', error)
      toast.error(error.message || 'Failed to download progress report')
    } finally {
      setDownloadingReport(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  const formattedTestDate = userTestDate
    ? new Date(userTestDate).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric'
      })
    : null
  const daysUntilTest = userTestDate
    ? Math.ceil((new Date(userTestDate) - new Date()) / (1000 * 60 * 60 * 24))
    : null
  const pointsToGoal = userGoal && predictedScore ? userGoal - predictedScore : null

  // Personalized Recommendations - defined here so it can render high in the coaching stack
  // (right under the goal) instead of at the very bottom of the page.
  const recommendationsSection = userGoal && (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-yellow-500" />
          Personalized Recommendations
        </h2>
        {recommendations?.gap > 0 && (
          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 text-sm font-medium rounded-full">
            {recommendations.gap} points to goal
          </span>
        )}
      </div>
      <div className="card-body">
        {recommendationsLoading ? (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : recommendations?.status === 'insufficient_data' ? (
          // Not enough practice data
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <BookOpen className="w-8 h-8 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Keep Practicing!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              Complete more challenges to unlock personalized recommendations based on your
              performance.
            </p>
            <button onClick={() => navigate('/challenge-type')} className="btn-primary">
              Start a Challenge
            </button>
          </div>
        ) : !recommendations ? (
          // A goal is set (outer gate guarantees userGoal), so this is a load failure, not a
          // missing goal - offer a retry instead of the misleading "set a goal" copy.
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
              <Target className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              We couldn&apos;t load your recommendations right now.
            </p>
            <button onClick={() => loadData()} className="btn-secondary">
              Retry
            </button>
          </div>
        ) : recommendations.status === 'on_track' ? (
          // On track to meet goal
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <Sparkles className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h3 className="text-lg font-semibold text-green-700 dark:text-green-300 mb-2">
              You're On Track!
            </h3>
            <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto mb-4">
              Your predicted score of{' '}
              <span className="font-bold text-green-600 dark:text-green-400">
                {recommendations.predictedScore}
              </span>{' '}
              exceeds your goal of {recommendations.goalScore}. Keep up the great work!
            </p>
            {recommendations.recommendations?.length > 0 && (
              <div className="mt-6 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-left">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300 mb-2">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">{recommendations.recommendations[0].title}</span>
                </div>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {recommendations.recommendations[0].description}
                </p>
              </div>
            )}
          </div>
        ) : (
          // Needs work - show actionable recommendations
          <div className="space-y-4">
            {/* Gap Summary */}
            <div className="p-4 bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-lg">
              <p className="text-gray-700 dark:text-gray-300">
                <span className="font-medium">Current prediction:</span>{' '}
                <span className="text-primary-600 dark:text-primary-400 font-bold">
                  {recommendations.predictedScore}
                </span>
                {' → '}
                <span className="font-medium">Goal:</span>{' '}
                <span className="text-purple-600 dark:text-purple-400 font-bold">
                  {recommendations.goalScore}
                </span>
              </p>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                {recommendations.message}
              </p>
            </div>

            {/* Weak Areas */}
            {recommendations.weakAreas?.length > 0 && (
              <div className="space-y-2">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-500" />
                  Areas Needing Focus
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {recommendations.weakAreas.map((area, index) => (
                    <div
                      key={index}
                      className="flex items-center justify-between p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800"
                    >
                      <span className="text-sm font-medium text-gray-800 dark:text-gray-200">
                        {area.domain}
                      </span>
                      <span
                        className={`text-sm font-bold ${
                          area.accuracy < 50
                            ? 'text-red-600 dark:text-red-400'
                            : area.accuracy < 70
                              ? 'text-amber-600 dark:text-amber-400'
                              : 'text-green-600 dark:text-green-400'
                        }`}
                      >
                        {Math.round(area.accuracy)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recommendations List */}
            {recommendations.recommendations?.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary-500" />
                  Recommended Actions
                </h4>
                {recommendations.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border ${
                      rec.priority === 'high'
                        ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                        : rec.priority === 'medium'
                          ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800'
                          : 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className={`p-2 rounded-lg ${
                          rec.priority === 'high'
                            ? 'bg-red-100 dark:bg-red-900/40'
                            : rec.priority === 'medium'
                              ? 'bg-amber-100 dark:bg-amber-900/40'
                              : 'bg-blue-100 dark:bg-blue-900/40'
                        }`}
                      >
                        {rec.type === 'domain_focus' ? (
                          <Target
                            className={`w-5 h-5 ${
                              rec.priority === 'high'
                                ? 'text-red-600 dark:text-red-400'
                                : rec.priority === 'medium'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-blue-600 dark:text-blue-400'
                            }`}
                          />
                        ) : rec.type === 'consistency' ? (
                          <TrendingUp
                            className={`w-5 h-5 ${
                              rec.priority === 'high'
                                ? 'text-red-600 dark:text-red-400'
                                : rec.priority === 'medium'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-blue-600 dark:text-blue-400'
                            }`}
                          />
                        ) : (
                          <BookOpen
                            className={`w-5 h-5 ${
                              rec.priority === 'high'
                                ? 'text-red-600 dark:text-red-400'
                                : rec.priority === 'medium'
                                  ? 'text-amber-600 dark:text-amber-400'
                                  : 'text-blue-600 dark:text-blue-400'
                            }`}
                          />
                        )}
                      </div>
                      <div className="flex-1">
                        <h5 className="font-semibold text-gray-900 dark:text-gray-100">
                          {rec.title}
                        </h5>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {rec.description}
                        </p>
                        {rec.potentialGain && (
                          <span className="inline-block mt-2 text-xs font-medium text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded">
                            +{rec.potentialGain} potential points
                          </span>
                        )}
                        {rec.actionType === 'focused_practice' && rec.actionData && (
                          <button
                            onClick={() => navigate('/challenge-type')}
                            className="mt-3 text-sm font-medium text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
                          >
                            {rec.action}
                            <ChevronRight className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium px-2 py-1 rounded ${
                          rec.priority === 'high'
                            ? 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200'
                            : rec.priority === 'medium'
                              ? 'bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200'
                              : 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200'
                        }`}
                      >
                        {rec.priority}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )

  return (
    <div className="max-w-6xl mx-auto space-y-6 pb-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Target className="w-7 h-7 text-primary-600" />
            My SAT Journey
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">
            Your goals, practice plan, strategy levels, and achievements. Everything to guide your
            next step.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={handleDownloadReport}
            disabled={downloadingReport}
          >
            {downloadingReport ? <LoadingSpinner size="sm" /> : <Download className="w-4 h-4" />}
            {downloadingReport ? 'Generating...' : 'Download Report'}
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => setShareModalOpen(true)}
          >
            <Share2 className="w-4 h-4" />
            Share Progress
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => navigate('/my-report')}
          >
            <BarChart3 className="w-4 h-4" />
            Full Report
          </button>
          <button
            className="btn-secondary flex items-center gap-2"
            onClick={() => navigate('/score-reports')}
          >
            <ClipboardList className="w-4 h-4" />
            Score Reports
          </button>
        </div>
      </div>

      {/* Load error banner - a failed or slow fetch must surface here, not silently blank the
          page and look like the goal/scores were reset. */}
      {loadError && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 dark:border-amber-900/40 dark:bg-amber-950/20">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <p className="text-sm text-amber-800 dark:text-amber-200">
              We could not load some of your progress data. Your goal and history are safe - this is
              just a loading hiccup.
            </p>
          </div>
          <button
            type="button"
            onClick={loadData}
            className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
          >
            <RefreshCw className="h-4 w-4" />
            Retry
          </button>
        </div>
      )}

      {/* 1. Scores - Hero (top of page) */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Your SAT Scores
            </h2>
          </div>
        </div>
        <div className="card-body">
          {perSubjectLoading ? (
            <div className="flex items-center justify-center py-8">
              <LoadingSpinner size="md" />
            </div>
          ) : perSubjectError ? (
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">{perSubjectError}</p>
              <button
                type="button"
                onClick={refreshPerSubject}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          ) : (
            <PerSubjectScoreDisplay
              mathData={math}
              readingWritingData={readingWriting}
              combinedData={combined}
              showCurrent={true}
              showPredicted={true}
              showTrend={true}
            />
          )}
        </div>
      </div>

      {/* 2. Score Trend - Combined Math & R&W (top of page) */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <BarChart2 className="w-5 h-5" />
            Score Trend
          </h2>
          <button
            onClick={() => setShareModalOpen(true)}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            <Share2 className="w-4 h-4" />
            Share
          </button>
        </div>
        <div className="card-body">
          {perSubjectLoading || actualHistoryLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner size="md" />
            </div>
          ) : perSubjectError ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertCircle className="h-6 w-6 text-amber-500" />
              <p className="text-sm text-gray-600 dark:text-gray-400">{perSubjectError}</p>
              <button
                type="button"
                onClick={refreshPerSubject}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                <RefreshCw className="h-4 w-4" />
                Retry
              </button>
            </div>
          ) : (
            <CombinedTrendChart
              // Actual scores (solid lines) from challenge history
              mathActualHistory={mathActual?.history || []}
              rwActualHistory={rwActual?.history || []}
              // Predicted/projected scores (dashed lines) from prediction history
              mathPredictedHistory={math?.trend?.history || []}
              rwPredictedHistory={readingWriting?.trend?.history || []}
              // Score report markers (SAT, PSAT, Practice)
              scoreReports={scoreReports}
              // Uncertainty bands
              mathUncertainty={math?.uncertaintyBand}
              rwUncertainty={readingWriting?.uncertaintyBand}
              // Goal visualization
              goalScore={userGoal}
              goalDate={practiceRecommendation?.testDate}
              height={280}
              showConfidenceBands={true}
              showProjections={true}
              onShare={() => setShareModalOpen(true)}
            />
          )}
        </div>
      </div>

      {/* 3. Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-lg flex items-center justify-center">
                <Target className="w-6 h-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Predicted Score</p>
                <p className="text-xl font-bold text-gray-900 dark:text-gray-100">
                  {predictedScore || 'Keep practicing'}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-body">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-yellow-100 dark:bg-yellow-900/30 rounded-lg flex items-center justify-center">
                <Trophy className="w-6 h-6 text-yellow-600 dark:text-yellow-400" />
              </div>
              <div>
                <p className="text-sm text-gray-600 dark:text-gray-400">Achievements</p>
                <button
                  onClick={() => navigate('/profile#achievements')}
                  className="text-xl font-bold text-gray-900 dark:text-gray-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors flex items-center gap-1"
                >
                  View All
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 4. AI Advisor - collapsed by default with a hint; self-hides when the feature is off or
          the student opted out. First item of the coaching stack. */}
      <AIAdvisorCard defaultCollapsed />

      {/* 5. Goal & Test Date - persistent panel, below the advisor */}
      <div className="card">
        <div className="card-body">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 flex-shrink-0 rounded-xl bg-gradient-to-br from-primary-100 to-purple-100 dark:from-primary-900/30 dark:to-purple-900/30 flex items-center justify-center">
                <Target className="w-6 h-6 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  Goal and Test Date
                </h2>
                {!goalLoaded && loadError ? (
                  <div className="mt-1 flex items-center gap-2 text-sm">
                    <AlertCircle className="h-4 w-4 text-amber-500" />
                    <span className="text-gray-600 dark:text-gray-400">
                      Could not load your goal. It is safe - retry to reload it.
                    </span>
                  </div>
                ) : userGoal ? (
                  <div className="mt-1 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm">
                    <span className="text-gray-600 dark:text-gray-400">
                      Target score{' '}
                      <span className="font-semibold text-purple-600 dark:text-purple-400">
                        {userGoal}
                      </span>
                    </span>
                    {formattedTestDate ? (
                      <span className="text-gray-600 dark:text-gray-400">
                        Test day{' '}
                        <span className="font-semibold text-gray-900 dark:text-gray-100">
                          {formattedTestDate}
                        </span>
                        {daysUntilTest > 0 ? ` (${daysUntilTest} days away)` : ''}
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Test date not set</span>
                    )}
                    {pointsToGoal !== null && (
                      <span className="font-medium text-primary-600 dark:text-primary-400">
                        {pointsToGoal <= 0
                          ? 'On track to reach your goal'
                          : `${pointsToGoal} points to go`}
                      </span>
                    )}
                  </div>
                ) : (
                  <p className="mt-1 max-w-md text-sm text-gray-600 dark:text-gray-400">
                    Set your target score and test date to unlock a personalized practice plan and
                    score trajectory. This is the starting point for everything below.
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={!goalLoaded && loadError ? loadData : () => setGoalModalOpen(true)}
              className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
            >
              {!goalLoaded && loadError ? (
                <>
                  <RefreshCw className="w-4 h-4" />
                  Retry
                </>
              ) : (
                <>
                  <Target className="w-4 h-4" />
                  {userGoal ? 'Edit Goal' : 'Set Goal Score'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* 6. Personalized Recommendations - directly under the goal, close to the advisor */}
      {recommendationsSection}

      {/* 7. Practice Plan + "Where your score is headed" - only when a goal is set */}
      {userGoal && practiceRecommendation && (
        <div className="space-y-6">
          <PracticePlanCard
            data={practiceRecommendation}
            onUpdateGoal={() => setGoalModalOpen(true)}
            onStartPractice={() => navigate('/challenge-type')}
          />
          {studyPlan && <StudyPlanProjectionChart data={studyPlan} />}
        </div>
      )}

      {/* 8. Strategy Levels Section */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Award className="w-5 h-5" />
            Strategy Levels
          </h2>
          <button
            onClick={() => navigate('/profile#progress')}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            View Details
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="card-body">
          {availableStrategies.length === 0 ? (
            <div className="text-center py-8">
              <Award className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
              <p className="text-gray-500 dark:text-gray-400">No strategies available</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {availableStrategies.map((strategy) => {
                const strategyType = strategy.strategyType || strategy.type
                if (!strategyType) return null

                const strategyLevel = strategyLevels.find(
                  (level) => level.strategyType === strategyType
                )

                if (strategyLevel) {
                  return (
                    <StrategyLevelCard
                      key={strategyType}
                      strategyLevel={strategyLevel}
                      onClick={() => navigate(`/strategy/${strategyType}`)}
                      compact={true}
                      publicName={user?.publicName}
                    />
                  )
                } else {
                  return (
                    <StrategyLevelCardEmpty
                      key={strategyType}
                      strategyType={strategyType}
                      onStart={() => navigate('/challenge-type')}
                    />
                  )
                }
              })}
            </div>
          )}
        </div>
      </div>

      {/* 9. Achievements Section */}
      <div className="card">
        <div className="card-header flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Trophy className="w-5 h-5" />
            Recent Achievements
          </h2>
          <button
            onClick={() => navigate('/profile#achievements')}
            className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center gap-1"
          >
            View All
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="card-body">
          <BadgeDisplay userId={user?.id} />
        </div>
      </div>

      <div className="mt-4">
        <StudentGroupBadges />
      </div>

      {/* Goal Setting Modal */}
      <GoalSettingModal
        isOpen={goalModalOpen}
        onClose={() => setGoalModalOpen(false)}
        currentGoal={userGoal}
        currentTestDate={userTestDate}
        predictedScore={predictedScore}
        onGoalSet={async (data) => {
          setUserGoal(data.goalSatScore)
          setUserTestDate(data.testDate)
          // Refresh BOTH recommendations after goal update
          try {
            const [recResponse, practiceResponse, studyPlanResponse] = await Promise.all([
              api.get('/predictions/recommendations'),
              api.get('/predictions/practice-recommendation'),
              api.get('/predictions/study-plan').catch(() => null)
            ])
            if (recResponse.data.success) {
              setRecommendations(recResponse.data.data)
            }
            if (practiceResponse.data.success) {
              setPracticeRecommendation(practiceResponse.data.data)
            }
            if (studyPlanResponse?.data?.success) {
              setStudyPlan(studyPlanResponse.data.data)
            }
          } catch (err) {
            logger.warn('Could not refresh recommendations:', err)
          }
        }}
      />

      {/* Share Progress Modal */}
      <ShareProgressModal
        isOpen={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        username={user?.publicName || user?.email?.split('@')[0] || ''}
      />
    </div>
  )
}

export default Progress
